import { Component, ComponentRef, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { Report } from '../../interfaces/report/report';
import { ServerResponse } from '../../interfaces/base/server.response';
import { Subject } from 'rxjs';
import { ActivatedRoute, Params } from '@angular/router';
import { TrackAndTraceService } from '../../services/reports/track-and-trace.service';
import { Gallery, GalleryItem, GalleryRef, ImageItem, VideoItem } from 'ng-gallery';
import { takeUntil } from 'rxjs/operators';
import { ReportAttachment } from '../../interfaces/report/report-attachment';
import { MatDialog } from '@angular/material/dialog';
import { LocationViewerDialogComponent } from '../../../@fury/shared/dialog/location-viewer/location-viewer-dialog.component';
import { fadeInUpAnimation } from '../../../@fury/animations/fade-in-up.animation';
import { AuthenticationService } from '../../services/base/authentication.service';
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { RemovableAttachmentComponent } from '../../../@fury/shared/removable-attachment/removable-attachment.component';
import { trim } from '../../../@fury/utils/trim';

@Component({
  selector: 'fury-track-and-trace',
  templateUrl: './track-and-trace.component.html',
  styleUrls: ['./track-and-trace.component.scss'],
  animations: [fadeInUpAnimation],
})
export class TrackAndTraceComponent implements OnInit, OnDestroy {

  public isLoading: boolean = null;
  public report: Report = null;

  public reportUniqueId: string = null;

  public galleryReportRef: GalleryRef = null;
  public galleryMedia: GalleryItem[];

  public galleryHandlingRef: GalleryRef = null;
  public handlingGalleryMedia: GalleryItem[];

  public showForm: boolean = false;
  @ViewChild('attachmentsPlaceholder', {read: ViewContainerRef}) attachmentsPlaceholder: ViewContainerRef;
  public attachmentsRef: Array<ComponentRef<RemovableAttachmentComponent>> = [];
  public attachmentIgnored: string = null;
  public attachmentCountExceeded: boolean = false;
  public attachmentTotalSizeExceeded: boolean = false;

  public acceptedMaxAttachments: number = 6;
  public acceptedMaxSizeAttachments: number = 31457280;
  private acceptedAttachments: string[] = ['jpg', 'jpeg', 'png', 'pdf', 'mp4', 'flv', 'doc', 'docx', 'xlsx', 'xls'];

  public formSubmitted: boolean = false;
  public formSubmitting: boolean = false;
  public formSubmitError: boolean = false;

  public uploadProgress: number = 0;

  public form = this.fb.group({
    description: new FormControl('', {nonNullable: true, validators: [Validators.required, trim(), Validators.pattern(/^(?!\s*$)(?!\d+\s*$).+/), Validators.maxLength(1000)]}),
    attachments: this.fb.array([]),
    attachmentsContainer: [''],
  });

  private destroy$: Subject<null> = new Subject<null>();

  constructor(private activatedRoute: ActivatedRoute,
              private fb: FormBuilder,
              public authenticationService: AuthenticationService,
              public trackAndTraceService: TrackAndTraceService,
              public gallery: Gallery,
              public dialog: MatDialog,
  ) {
  }

  ngOnInit(): void {
    document.documentElement.style.setProperty('--primary-color', '#1b143c');
    document.documentElement.style.setProperty('--primary-color-text', '#ffffff');

    this.galleryReportRef = this.gallery.ref('report');
    this.galleryHandlingRef = this.gallery.ref('handling');

    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params: Params): void => {
      if (typeof params['id'] !== 'undefined') {
        if (params['id'] !== null) {
          this.reportUniqueId = params['id'];
          this.isLoading = true;

          this.getData(params['id']);
        }
      }
    });
  }

  getData(reportUniqueId: string): void {
    this.trackAndTraceService.get(reportUniqueId).subscribe(
      (response: ServerResponse): void => {
        if (typeof response.data !== 'undefined') {
          this.report = response.data;

          if (this.report.attachments.total && this.report.attachments.media.length) {
            this.galleryMedia = this.report.attachments.media.map((item: ReportAttachment): VideoItem | ImageItem => {
              if (item.type === 'video') {
                return new VideoItem({
                  src: <any>[
                    {
                      url: item.src,
                      type: item.mime,
                    }
                  ],
                  thumb: item.thumb,
                });
              } else {
                return new ImageItem({
                  src: item.src,
                  thumb: item.thumb,
                });
              }
            });
            this.galleryReportRef.load(this.galleryMedia);
          }

          if (this.report.handling.attachments.total && this.report.handling.attachments.media.length) {
            this.handlingGalleryMedia = this.report.handling.attachments.media.map((item: ReportAttachment): VideoItem | ImageItem => {
              if (item.type === 'video') {
                return new VideoItem({
                  src: <any>[
                    {
                      url: item.src,
                      type: item.mime,
                    }
                  ],
                  thumb: item.thumb,
                });
              } else {
                return new ImageItem({
                  src: item.src,
                  thumb: item.thumb,
                });
              }
            });
            this.galleryHandlingRef.load(this.handlingGalleryMedia);
          }

        }

        this.isLoading = false;
      },
      (): void => {
        this.isLoading = false;
      }
    );
  }

  viewLocation(coords: { lat: number, lng: number }): void {
    this.dialog.open(LocationViewerDialogComponent, {
      data: coords
    });
  }

  downloadAttachment(attachmentId: number): void {
    this.trackAndTraceService.downloadAttachment(this.reportUniqueId, attachmentId);
  }

  downloadHandlingAttachment(attachmentId: number): void {
    this.trackAndTraceService.downloadHandlingAttachment(this.reportUniqueId, attachmentId);
  }

  toggleForm(): void {
    this.form.reset();
    this.showForm = !this.showForm;
  }

  detectUploadAttachments(event: any): void {
    if (typeof event !== 'undefined') {
      if (typeof event.target !== 'undefined') {
        if (typeof event.target.files !== 'undefined') {
          const formArray: FormArray = this.form.get('attachments') as FormArray;
          let totalSize: number = 0;

          Object.keys(this.form.controls.attachments.value).map((d: string): void => {
            totalSize += this.form.controls.attachments.value[d].size;
          });

          Object.keys(event.target.files).map((file: string): void => {
            if (this.form.controls.attachments.length < this.acceptedMaxAttachments) {
              if (this.acceptedAttachments.includes(event.target.files[file].name.split('.').pop().toLowerCase())) {
                if ((totalSize += event.target.files[file].size) < this.acceptedMaxSizeAttachments) {
                  const component: ComponentRef<RemovableAttachmentComponent> = this.attachmentsPlaceholder.createComponent(RemovableAttachmentComponent);
                  component.instance.filename = event.target.files[file].name;
                  component.instance.size = event.target.files[file].size;

                  this.attachmentsRef.push(component);

                  const control: FormControl<File> = new FormControl(event.target.files[file]);
                  formArray.push(control);
                  component.instance.control = control;

                  component.instance.destroy.subscribe((): void => {
                    formArray.removeAt(formArray.controls.indexOf(component.instance.control));
                    component.destroy();
                  });
                } else {
                  this.attachmentTotalSizeExceeded = true;
                  setTimeout((): boolean => this.attachmentTotalSizeExceeded = false, 10000);
                }
              } else {
                this.attachmentIgnored = event.target.files[file].name;
                setTimeout((): void => this.attachmentIgnored = null, 10000);
              }
            } else {
              this.attachmentCountExceeded = true;
              setTimeout((): boolean => this.attachmentCountExceeded = false, 10000);
            }
          });
        }
      }
    }
  }

  submit(): void {
    this.formSubmitting = true;
    this.formSubmitError = false;

    this.trackAndTraceService.save(this.reportUniqueId, this.form.value).subscribe((value: number | boolean | Report): void => {
      if (typeof value === 'number') {
        this.uploadProgress = value;
      } else if (value === false) {
          this.formSubmitting = false;
          this.formSubmitError = true;
      } else {
        this.formSubmitting = false;
        this.formSubmitted = true;
        this.report = <Report>value;
        setTimeout((): void => {
          this.showForm = false;
          this.formSubmitted = false;
        }, 8000);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.isLoading = false;
  }
}
